import { type ReactNode } from 'react';
import * as React from 'react';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { ReactComponent as ArrowRight } from '../../images/icons/icon-arrow-right-large.svg';

import { LinkWithQuery } from './LinkWithQuery';

const LearnMore = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  margin-top: auto;
  padding: 0 10px 10px;
  opacity: 0.4;
  transition: opacity ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};

  span {
    font-family: ${({ theme }) => theme.fontFamily.MONO};
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    font-weight: ${({ theme }) => theme.fontWeight.BOLD};
    text-transform: uppercase;
    line-height: 11px;
    opacity: 0;
    transition: opacity ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING};
  }
`;

const Wrapper = styled(LinkWithQuery)<{ $showBkgGradient?: boolean }>`
  display: flex;
  flex: 1 1 0;
  padding: 0;
  position: relative;
  cursor: pointer;
  box-shadow: 0 0 0 1px transparent;
  border: none;

  transition:
    box-shadow ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING},
    background-color ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING};

  ${({ $showBkgGradient }) =>
    $showBkgGradient &&
    css`
      &::before {
        content: '';
        top: 0;
        left: 0;
        right: 0;
        position: absolute;
        height: 165px;
        background: linear-gradient(
          349deg,
          transparent 30%,
          rgba(157, 123, 138, 0.822275) 92%
        );
      }
    `}

  &::after {
    content: '';
    background: ${({ theme }) => theme.highlightColor};
    opacity: 0.05;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
    transition: opacity ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING};
  }

  &:hover {
    background: transparent;
    box-shadow: 0 0 0 1px ${({ theme }) => rgba(theme.highlightColor, 0.3)};

    &::after {
      opacity: 0.1;
    }

    ${LearnMore} {
      opacity: 1;

      > span {
        opacity: 1;
      }
    }
  }

  &:active {
    box-shadow: 0 0 0 1px ${({ theme }) => rgba(theme.highlightColor, 0.4)};

    &::after {
      opacity: 0.4;
    }
  }
`;

const Inner = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
`;

export interface GridItemType {
  description: string;
  illustration?: ReactNode;
  link: string;
  showBkgGradient?: boolean;
  title: ReactNode;
}

interface GridItemProps {
  DANGEROUS_AS_DIV?: boolean;
  children: ReactNode;
  className?: string;
  isExternal?: boolean;
  showBkgGradient?: boolean;
  showLearnMore?: boolean;
  to: string;
}

export const GridItem: React.ComponentType<GridItemProps> = React.memo(
  ({
    showBkgGradient = false,
    showLearnMore = true,
    to,
    isExternal = false,
    children,
    className,
    DANGEROUS_AS_DIV
  }) => {
    return (
      <Wrapper
        $showBkgGradient={showBkgGradient}
        DANGEROUS_AS_DIV={DANGEROUS_AS_DIV}
        className={className}
        isExternal={isExternal}
        to={to}>
        <Inner>
          {children}
          {showLearnMore && (
            <LearnMore>
              <span>Learn more</span>
              <ArrowRight />
            </LearnMore>
          )}
        </Inner>
      </Wrapper>
    );
  }
);
